class ResponsiveBackgroundVideo extends HTMLElement {
  constructor() {
    super();
    this.currentVideo = null;
    this.currentViewport = null;
    this.resizeTimeout = null;
    this.currentDeferredMedia = null;
  }

  connectedCallback() {
    // Parse video data from attributes
    this.videoData = {
      desktop: {
        image: this.dataset.desktopImageUrl,
        imageAlt: this.dataset.desktopImageAlt,
        shopify: this.dataset.videoShopifyDesktopUrl,
        shopifyId: this.dataset.videoShopifyDesktopId,
        external: this.dataset.videoExternalDesktop,
        isShopify: this.dataset.useVideoTagDesktop === 'true'
      },
      mobile: {
        shopify: this.dataset.videoShopifyMobileUrl,
        shopifyId: this.dataset.videoShopifyMobileId,
        external: this.dataset.videoExternalMobile,
        isShopify: this.dataset.useVideoTagMobile === 'true'
      }
    };

    // Log summary of available content
    const hasDesktopImage = !!this.videoData.desktop.image;
    const hasDesktopVideo = !!(this.videoData.desktop.shopify || this.videoData.desktop.external);
    const hasMobileVideo = !!(this.videoData.mobile.shopify || this.videoData.mobile.external);
    console.log(`[ResponsiveBackgroundVideo] Content available - Desktop Image: ${hasDesktopImage}, Desktop Video: ${hasDesktopVideo}, Mobile Video: ${hasMobileVideo}`);

    this.sectionId = this.dataset.sectionId;
    this.videoDescription = this.dataset.videoDescription || '';
    
    // Initial load
    this.loadAppropriateVideo();
    
    // Handle resize with debouncing
    this.resizeHandler = () => {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        this.checkAndUpdateVideo();
      }, 250);
    };
    
    window.addEventListener('resize', this.resizeHandler);
  }

  getViewportType() {
    return window.innerWidth >= 768 ? 'desktop' : 'mobile';
  }

  loadAppropriateVideo() {
    const viewport = this.getViewportType();
    const data = this.videoData[viewport];
    
    // For desktop, prioritize image over video
    if (viewport === 'desktop' && data.image) {
      console.log(`[ResponsiveBackgroundVideo] Loading desktop image for desktop viewport`);
      this.loadImage('desktop', data);
      return;
    }
    
    // If no video for current viewport, fall back to other viewport
    if (!data.shopify && !data.external) {
      const fallbackViewport = viewport === 'desktop' ? 'mobile' : 'desktop';
      const fallbackData = this.videoData[fallbackViewport];
      if (fallbackData.shopify || fallbackData.external) {
        console.log(`[ResponsiveBackgroundVideo] Loading ${fallbackViewport} video as fallback for ${viewport} viewport`);
        this.loadVideo(fallbackViewport, fallbackData);
      }
    } else if (data.shopify || data.external) {
      console.log(`[ResponsiveBackgroundVideo] Loading ${viewport} video for ${viewport} viewport`);
      this.loadVideo(viewport, data);
    }
  }

  async loadImage(viewport, data) {
    // Don't reload if same image is already loaded
    if (this.currentViewport === viewport && this.querySelector('img')) {
      return;
    }
    
    this.currentViewport = viewport;
    
    // Clear existing content
    this.innerHTML = '';
    
    // Create image element
    const img = document.createElement('img');
    img.src = data.image;
    img.alt = data.imageAlt || '';
    img.style.width = '100%';
    img.style.height = '100%';
    img.style.objectFit = 'cover';
    img.style.display = 'block';
    
    this.appendChild(img);
    
    console.log(`[ResponsiveBackgroundVideo] Desktop image loaded successfully`);
  }

  async loadVideo(viewport, videoData) {
    // Don't reload if same video is already loaded
    if (this.currentViewport === viewport && this.currentDeferredMedia) {
      return;
    }
    
    this.currentViewport = viewport;
    
    // Clear existing video
    if (this.currentDeferredMedia) {
      // Clean up existing video component
      const existingVideoComponent = this.querySelector('video-component');
      if (existingVideoComponent && existingVideoComponent.pauseVideo) {
        existingVideoComponent.pauseVideo();
      }
    }
    
    this.innerHTML = '';
    
    // Create video container
    const container = document.createElement('div');
    container.className = videoData.isShopify ? '' : 'video-section__bg-iframe-video';
    
    // Create deferred media wrapper
    const deferredMedia = document.createElement('deferred-media');
    this.currentDeferredMedia = deferredMedia;
    
    const template = document.createElement('template');
    
    // Create video component
    const videoComponent = document.createElement('video-component');
    videoComponent.className = videoData.isShopify ? 'has-video' : 'has-iframe';
    videoComponent.dataset.videoId = `${this.sectionId}-${viewport}`;
    videoComponent.dataset.autoplay = 'true';
    videoComponent.dataset.background = 'true';
    videoComponent.dataset.description = this.videoDescription;
    
    if (videoData.isShopify && videoData.shopify) {
      // For Shopify-hosted videos
      const video = document.createElement('video');
      video.autoplay = true;
      video.muted = true;
      video.loop = true;
      video.playsinline = true;
      video.setAttribute('playsinline', ''); // Explicit attribute for iOS
      video.setAttribute('webkit-playsinline', ''); // Legacy iOS support
      video.setAttribute('autoplay', ''); // Explicit attribute
      video.setAttribute('muted', ''); // Explicit attribute
      video.setAttribute('loop', ''); // Explicit attribute
      video.poster = '';
      video.preload = 'auto'; // Preload video for better mobile performance
      
      // Add source element for better compatibility
      const source = document.createElement('source');
      source.src = videoData.shopify;
      source.type = 'video/mp4';
      video.appendChild(source);
      
      videoComponent.appendChild(video);
    } else if (videoData.external) {
      // For external videos (YouTube/Vimeo)
      videoComponent.dataset.videoUrl = videoData.external;
      const div = document.createElement('div');
      videoComponent.appendChild(div);
    }
    
    template.content.appendChild(videoComponent);
    deferredMedia.appendChild(template);
    container.appendChild(deferredMedia);
    this.appendChild(container);
    
    // Force immediate loading by setting the loaded attribute and appending content directly
    const templateContent = template.content.cloneNode(true);
    deferredMedia.appendChild(templateContent);
    deferredMedia.setAttribute('loaded', '');
    
    // Wait for the video component to be connected and initialized
    requestAnimationFrame(() => {
      const videoComponent = this.querySelector('video-component');
      
      if (videoComponent) {
        // For native video elements, ensure autoplay
        const video = videoComponent.querySelector('video');
        if (video) {
          // Ensure all autoplay requirements are met
          video.muted = true;
          video.defaultMuted = true;
          video.setAttribute('muted', '');
          
          // Try multiple play strategies for better mobile compatibility
          const attemptPlay = () => {
            const playPromise = video.play();
            
            if (playPromise !== undefined) {
              playPromise
                .then(() => {
                  console.log('[ResponsiveBackgroundVideo] Video autoplay started');
                })
                .catch(err => {
                  console.error('[ResponsiveBackgroundVideo] Video autoplay failed:', err);
                  // Try playing on first user interaction as fallback
                  const playOnInteraction = () => {
                    video.play().catch(() => {});
                    document.removeEventListener('touchstart', playOnInteraction);
                    document.removeEventListener('click', playOnInteraction);
                  };
                  document.addEventListener('touchstart', playOnInteraction, { once: true });
                  document.addEventListener('click', playOnInteraction, { once: true });
                });
            }
          };
          
          // Try playing immediately and after a small delay
          attemptPlay();
          setTimeout(attemptPlay, 100);
        }
        
        // If the video component has an init method, call it
        if (videoComponent.init) {
          videoComponent.init();
        }
      }
    });
    
    console.log(`[ResponsiveBackgroundVideo] ${viewport} video loaded successfully`);
  }

  checkAndUpdateVideo() {
    const newViewport = this.getViewportType();
    if (newViewport !== this.currentViewport) {
      this.loadAppropriateVideo();
    }
  }

  disconnectedCallback() {
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
    
    // Clean up video component
    const videoComponent = this.querySelector('video-component');
    if (videoComponent && videoComponent.pauseVideo) {
      videoComponent.pauseVideo();
    }
  }
}

// Define the custom element
customElements.define('responsive-background-video', ResponsiveBackgroundVideo);